import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  Calendar, 
  FileText, 
  DollarSign, 
  Settings, 
  Plus,
  Search,
  Edit,
  Eye
} from 'lucide-react';

const PractitionerDashboard = () => {
  const { user, logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clients');
  const [newNote, setNewNote] = useState({
    session_date: '',
    session_type: 'consultation',
    notes: ''
  });

  useEffect(() => {
    if (user?.role === 'practitioner') {
      fetchClients();
    }
  }, [user]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/clients`, {
        credentials: 'include',
      });
      if (response.ok) {
        const clientsData = await response.json();
        setClients(clientsData);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientNotes = async (clientId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/notes/${clientId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const notes = await response.json();
        setClientNotes(notes);
      }
    } catch (error) {
      console.error('Error fetching client notes:', error);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    fetchClientNotes(client.id);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!selectedClient || !newNote.notes.trim()) {
      alert('Please select a client and enter session notes');
      return;
    }

    setLoading(true);
    try {
      // Ensure date is in YYYY-MM-DD format without timezone conversion
      let sessionDate = newNote.session_date;
      if (!sessionDate) {
        // Get today's date in local timezone as YYYY-MM-DD
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        sessionDate = `${year}-${month}-${day}`;
      }

      const noteData = {
        client_id: selectedClient.id,
        session_date: sessionDate, // Send exactly as entered, no conversion
        notes: newNote.notes.trim(),
        session_type: newNote.session_type || 'consultation'
      };

      console.log('Sending note data:', noteData); // Debug log

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/notes`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      });

      if (response.ok) {
        const note = await response.json();
        setClientNotes([note, ...clientNotes]);
        setNewNote({ session_date: '', session_type: 'consultation', notes: '' });
        console.log('Note added successfully:', note); // Debug log
        alert('Note added successfully!');
      } else {
        let errorMessage = 'Unknown error occurred';
        try {
          const errorData = await response.json();
          console.error('Error response:', errorData); // Debug log
          errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        alert(`Failed to add note: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Network error adding note:', error);
      alert(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'practitioner') {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-purple-800 mb-4 font-title">Access Denied</h1>
          <p className="text-lg text-purple-700 font-body">This area is restricted to practitioners only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-purple-800 font-title">Practitioner CRM</h1>
              <p className="text-gray-600 font-body">Welcome, {user.name}</p>
            </div>
            <Button onClick={logout} variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50">
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-8 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'clients'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <Users className="h-4 w-4 inline-block mr-2" />
            Client Management
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'appointments'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <Calendar className="h-4 w-4 inline-block mr-2" />
            Appointments
          </button>
          <button
            onClick={() => setActiveTab('invoicing')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'invoicing'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            <DollarSign className="h-4 w-4 inline-block mr-2" />
            Invoicing
          </button>
        </div>

        {/* Client Management Tab */}
        {activeTab === 'clients' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Client List */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Clients</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  </div>
                ) : clients.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No clients yet</p>
                    <p className="text-sm">Clients will appear here after your first session</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {clients.map((client, index) => (
                      <div
                        key={client.id}
                        onClick={() => handleClientSelect(client)}
                        className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                          selectedClient?.id === client.id
                            ? 'border-purple-400 shadow-lg transform scale-105'
                            : 'border-transparent hover:border-purple-200 hover:shadow-md'
                        } ${
                          index % 2 === 0 
                            ? 'bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-150' 
                            : 'bg-gradient-to-r from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-150'
                        }`}
                      >
                        <div className="font-bold text-gray-900 text-lg">{client.name}</div>
                        <div className="text-sm text-gray-600 font-medium">{client.email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Phone: {client.phone_number || 'Not provided'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Details & Notes */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5" />
                  <span>
                    {selectedClient ? `${selectedClient.name} - Session Notes` : 'Select a Client'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedClient ? (
                  <div className="space-y-6">
                    {/* Add New Note Form */}
                    <form onSubmit={handleAddNote} className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-3">Add Session Note</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Session Date
                          </label>
                          <Input
                            type="date"
                            value={newNote.session_date}
                            onChange={(e) => setNewNote({...newNote, session_date: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Session Type
                          </label>
                          <Select 
                            value={newNote.session_type} 
                            onValueChange={(value) => setNewNote({...newNote, session_type: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="consultation">Consultation</SelectItem>
                              <SelectItem value="reading">Reading</SelectItem>
                              <SelectItem value="healing">Healing Session</SelectItem>
                              <SelectItem value="coaching">Life Coaching</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Session Notes *
                        </label>
                        <Textarea
                          value={newNote.notes}
                          onChange={(e) => setNewNote({...newNote, notes: e.target.value})}
                          placeholder="Enter session notes, insights, recommendations..."
                          rows={4}
                          required
                        />
                      </div>
                      <Button 
                        type="submit"
                        className="bg-purple-600 hover:bg-purple-700"
                        disabled={loading || !newNote.notes.trim()}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {loading ? 'Adding Note...' : 'Add Note'}
                      </Button>
                    </form>

                    {/* Existing Notes */}
                    <div>
                      <h4 className="font-semibold mb-3">Session History</h4>
                      {clientNotes.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>No session notes yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {clientNotes.map((note) => (
                            <div key={note.id} className="bg-white border rounded-lg p-4">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {note.session_type.charAt(0).toUpperCase() + note.session_type.slice(1)}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {/* Display date as-is without conversion */}
                                    {note.session_date}
                                  </div>
                                </div>
                                <div className="flex space-x-2">
                                  <Button size="sm" variant="ghost">
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              <p className="text-gray-700 whitespace-pre-wrap">{note.notes}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">Select a client to view their information and session notes</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>Appointment Management</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Acuity Integration Coming Soon</h3>
                <p className="text-gray-500 mb-4">
                  Your appointment management will be integrated with Acuity Scheduling to provide 
                  seamless booking and calendar management.
                </p>
                <Button 
                  onClick={() => window.open('https://thewisdomnetwork.as.me/', '_blank')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  View Current Bookings (Acuity)
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoicing Tab */}
        {activeTab === 'invoicing' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5" />
                <span>Invoicing & Payments</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <DollarSign className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Stripe Integration Coming Soon</h3>
                <p className="text-gray-500 mb-4">
                  Create and send invoices, track payments, and manage your earnings all in one place.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-700">Create Invoices</h4>
                    <p className="text-sm text-gray-500">Send professional invoices to clients</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-700">Track Payments</h4>
                    <p className="text-sm text-gray-500">Monitor payment status and history</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-700">Earnings Dashboard</h4>
                    <p className="text-sm text-gray-500">View your financial performance</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PractitionerDashboard;